import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ml_queue_api } from "../ml/mlApiHelper.js";

const baseURL = process.env.REACT_APP_DASHBOARD_API_URL;
const DASHBOARD_API =
  process.env.REACT_APP_DASHBOARD_API_URL || "http://localhost:8200/dashboard";
const mlBaseURL = `${DASHBOARD_API}/livechat/ml`;
const mlQueueBaseURL = `${DASHBOARD_API}/livechat/ml-queue`;
// Static files (e.g. TTS audio) are mounted at the backend's root, not under /dashboard.
const apiRootURL = DASHBOARD_API.replace(/\/dashboard\/?$/, "");

const clientCode =
  "gAAAAABn2ZDB-lUaoTDWz4vXRg71RAOREYW_CuUh9YeXAWUFU2kLaGIotchJwZdks5Hzam43sWkOkmfhYcKHBafnhyuzr6agz72YH0assBuWx8uTa5nwWxcpJwm1OO_yxyis7OTb80gER_au-XwrGUyomSxm1_SCfQ==";
const domain = "livechat.effie.cx";
const postUserInfoUrl = `${baseURL}/user-information`;
const getInfoUrl = `${baseURL}/user-information`;

const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const DiagnosisPopup = ({ isOpen, onClose }) => {
  const [isUserInfoOpen, setIsUserInfoOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [diagnosis, setDiagnosis] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]); // New state for conversation history
  const [isMuted, setIsMuted] = useState(true);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Check if input has at least 15 characters
  const isValidInput = () => {
    return symptoms.trim().length >= 0;
  };

  useEffect(() => {
    const checkUserInfo = async () => {
      const storedName = localStorage.getItem("patient_name");
      const storedEmail = localStorage.getItem("patient_email");

      if (storedName && storedEmail) {
        setName(storedName);
        setEmail(storedEmail);
        setIsUserInfoOpen(false);
        return;
      }

      let sessionID = localStorage.getItem("session_id");
      if (!sessionID) {
        setIsUserInfoOpen(true);
        return;
      }

      try {
        const getInfoResponse = await fetch(getInfoUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_code: clientCode,
            domain_name: domain,
          }),
        });

        if (getInfoResponse.ok) {
          const userInfo = await getInfoResponse.json();
          setName(userInfo.name);
          setEmail(userInfo.email);
          localStorage.setItem("patient_id", userInfo.id);
          localStorage.setItem("patient_name", userInfo.name);
          localStorage.setItem("patient_email", userInfo.email);
          setIsUserInfoOpen(false);
        } else {
          console.error("Failed to retrieve user information");
          setIsUserInfoOpen(true);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
        setIsUserInfoOpen(true);
      }
    };

    if (isOpen) {
      checkUserInfo();
    }
  }, [isOpen]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [diagnosis]);

  // Save user info to database
  const handleUserInfoSubmit = async () => {
    if (name.trim() && email.trim()) {
      const sessionID = generateUUID();
      localStorage.setItem("session_id", sessionID);

      const data = {
        session_id: sessionID,
        email: email,
        name: name,
        client_code: clientCode,
        domain_name: domain,
      };

      try {
        const response = await fetch(postUserInfoUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        if (response.ok) {
          localStorage.setItem("patient_name", name);
          localStorage.setItem("patient_email", email);
          setIsUserInfoOpen(false);
        } else {
          console.error("Failed to send user information");
          alert("Failed to save user information. Please try again.");
        }
      } catch (error) {
        console.error("Error saving user info:", error);
        alert("An error occurred while saving your info. Please try again.");
      }
    } else {
      alert("Please fill out all fields.");
    }
  };

  // Handle symptom submission to diagnosis API
  const handleSubmitSymptoms = async () => {
    if (!isValidInput()) return;

    // Add user message to conversation history
    const newUserMessage = { role: "user", content: symptoms };
    setConversationHistory((prev) => [...prev, newUserMessage]);

    const currentSymptoms = symptoms;
    setSymptoms("");

    // Show the user's message immediately, with a pending/loading entry
    // for the assistant's reply while the job is queued and polled.
    const entryId = generateUUID();
    setDiagnosis((prev) => [
      ...prev,
      { id: entryId, symptoms: currentSymptoms, result: null, loading: true },
    ]);

    const updateEntry = (updates) => {
      setDiagnosis((prev) =>
        prev.map((item) =>
          item.id === entryId ? { ...item, ...updates } : item,
        ),
      );
    };

    const onError = (error) => {
      console.error("Error fetching diagnosis:", error);
      const errorMessage = {
        role: "assistant",
        content: "Error fetching diagnosis",
      };
      setConversationHistory((prev) => [...prev, errorMessage]);
      updateEntry({ result: "Error fetching diagnosis", loading: false });
    };

    try {
      await ml_queue_api(
        `${mlQueueBaseURL}/database/notes_disease_prediction`,
        {
          patient_conversation_id: localStorage.getItem("patient_id") || 1,
          client_code: clientCode,
          domain_name: domain,
          history: conversationHistory.slice(-6).concat(newUserMessage),
        },
        async (jobResult) => {
          // jobResult is the finished job's `result` object, e.g.
          // { success, results: { disease: [...] } }
          if (jobResult?.success === false) {
            onError(jobResult?.message || "Diagnosis job failed");
            return;
          }

          const diseaseList =
            jobResult?.results?.disease ||
            jobResult?.disease ||
            (Array.isArray(jobResult) ? jobResult : []);
          const resultText = diseaseList.join(", ") || "No diagnosis found";

          const assistantMessage = {
            role: "assistant",
            content: resultText,
          };

          // Update conversation history with assistant response
          setConversationHistory((prev) => [...prev, assistantMessage]);

          // Update diagnosis entry for UI display
          updateEntry({ result: resultText, loading: false });
          if (!isMuted) await handleTTS(resultText);
        },
        onError,
        mlQueueBaseURL,
      );
    } catch (error) {
      onError(error);
    }
  };

  // Handle text-to-speech (TTS)
  const handleTTS = async (text) => {
    try {
      const ttsResponse = await fetch(`${mlBaseURL}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: text, voice: "af" }),
      });
      if (!ttsResponse.ok) throw new Error("TTS API error");
      const { audio_path } = await ttsResponse.json();
      const audioUrl = `${apiRootURL}${audio_path}`;
      const audio = new Audio(audioUrl);
      await audio.play();
    } catch (error) {
      console.error("Error in TTS:", error);
    }
  };

  // Handle mute/unmute
  const toggleMute = () => setIsMuted(!isMuted);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {isUserInfoOpen ? (
            <motion.div
              className="bg-gray-900 text-white rounded-xl shadow-2xl p-8 w-full max-w-md border border-gray-700"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <h2 className="text-2xl font-bold mb-6 text-gray-100">
                Welcome to Diagnosis Tool
              </h2>
              <p className="mb-6 text-gray-400 text-sm">
                Please provide your details to continue.
              </p>
              <div className="space-y-5">
                <input
                  type="text"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3 bg-gray-800 rounded-lg border border-gray-600 focus:outline-none focus:border-blue-400 text-gray-200 placeholder-gray-500 transition duration-200"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 bg-gray-800 rounded-lg border border-gray-600 focus:outline-none focus:border-blue-400 text-gray-200 placeholder-gray-500 transition duration-200"
                />
                <div className="flex justify-between gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 text-gray-200 transition duration-200 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUserInfoSubmit}
                    className="flex-1 py-2 bg-blue-600 rounded-lg hover:bg-blue-500 text-white transition duration-200 text-sm font-medium"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              className="bg-gray-900 text-white rounded-xl shadow-2xl p-6 w-full max-w-md h-[65vh] flex flex-col border border-gray-700"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-100">
                  Diagnosis Tool
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleMute}
                    className="p-2 bg-transparent rounded-full hover:bg-gray-700/50 transition duration-200"
                  >
                    <i
                      className={`fas ${
                        isMuted ? "fa-volume-mute" : "fa-volume-up"
                      } text-gray-300`}
                    ></i>
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 bg-transparent rounded-full hover:bg-gray-700/50 transition duration-200"
                  >
                    <i className="fas fa-times text-gray-300"></i>
                  </button>
                </div>
              </div>
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700 space-y-4"
              >
                <div className="space-y-2">
                  <p className="text-gray-200 p-4 rounded-lg bg-gray-700/80 text-sm leading-relaxed">
                    Hello, {name || "User"}! Please describe your symptoms in
                    3-5 words or more. The more detail you provide, the more
                    accurate your diagnosis will be.
                  </p>
                  <p className="text-gray-400 p-4 rounded-lg bg-gray-700/80 text-xs leading-relaxed italic">
                    Disclaimer: This diagnosis is for demonstration purposes
                    only and should not be considered medical advice.
                  </p>
                </div>
                {diagnosis.map((item, index) => (
                  <div key={item.id || index} className="space-y-3">
                    <div className="text-right">
                      <p className="inline-block p-4 bg-blue-500/90 rounded-lg max-w-[80%] text-sm text-white leading-relaxed text-left">
                        {item.symptoms}
                      </p>
                    </div>
                    <div className="text-left">
                      {item.loading ? (
                        <div className="inline-flex items-center gap-1 p-4 bg-gray-700/80 rounded-lg">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                        </div>
                      ) : (
                        <p className="inline-block p-4 bg-gray-700/80 rounded-lg max-w-[80%] text-sm text-gray-200 leading-relaxed">
                          {item.result}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={symptoms}
                  onChange={(e) => setSymptoms(e.target.value)}
                  placeholder="Enter symptoms (min 15 characters)..."
                  className="w-full p-4 pr-12 bg-gray-800 rounded-lg border border-gray-600 focus:outline-none focus:border-blue-400 text-gray-200 placeholder-gray-500 text-sm transition duration-200"
                  onKeyPress={(e) =>
                    e.key === "Enter" &&
                    isValidInput() &&
                    handleSubmitSymptoms()
                  }
                />
                <button
                  onClick={handleSubmitSymptoms}
                  disabled={!isValidInput()}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 transition duration-200 ${
                    isValidInput()
                      ? "text-blue-400 hover:text-blue-300"
                      : "text-blue-400/50 cursor-not-allowed"
                  }`}
                >
                  <i className="fas fa-paper-plane"></i>
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DiagnosisPopup;
